import React, { useState, useEffect } from "react";
import { api, post } from "../api";
import { useAuth } from "../context/AuthContext";

interface VistaUser {
  user_id: string;
  email: string;
  display_name: string;
  role: string;
  allowed_customers: string[];
  last_login_at: string | null;
  created_at: string;
}

interface CustomerKey {
  key: string;
  name: string;
}

export default function Admin() {
  const { user } = useAuth();
  const [users, setUsers] = useState<VistaUser[]>([]);
  const [customerKeys, setCustomerKeys] = useState<CustomerKey[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [form, setForm] = useState({ email: "", password: "", display_name: "", role: "viewer", allowed_customers: [] as string[] });
  const [msg, setMsg] = useState("");
  const [budgets, setBudgets] = useState<Record<string, any>>({});
  const [budgetForm, setBudgetForm] = useState({ customer: "", amount: "" });

  const load = () => {
    api<VistaUser[]>("/api/admin/users").then(setUsers).catch(() => {});
    api<CustomerKey[]>("/api/admin/customer-keys").then((keys) => {
      setCustomerKeys(keys);
      // Load budgets for all customers
      keys.forEach(ck => {
        api(`/api/budgets/${ck.key}`).then(b => {
          if (b) setBudgets(prev => ({ ...prev, [ck.key]: b }));
        }).catch(() => {});
      });
    }).catch(() => {});
  };

  useEffect(load, []);

  if (user?.role !== "admin") {
    return <div className="text-center py-20 text-gray-500">Admin access required</div>;
  }

  const handleCreate = async () => {
    setMsg("");
    try {
      await post("/api/admin/users", form);
      setMsg("User created");
      setShowCreate(false);
      setForm({ email: "", password: "", display_name: "", role: "viewer", allowed_customers: [] });
      load();
    } catch (err: any) { setMsg(err.message); }
  };

  const handleUpdate = async (userId: string, updates: any) => {
    try {
      await api(`/api/admin/users/${encodeURIComponent(userId)}`, { method: "PUT", body: JSON.stringify(updates) });
      setEditingUser(null);
      load();
    } catch (err: any) { setMsg(err.message); }
  };

  const handleDelete = async (userId: string, email: string) => {
    if (!confirm(`Delete user ${email}?`)) return;
    await api(`/api/admin/users/${encodeURIComponent(userId)}`, { method: "DELETE" });
    load();
  };

  const toggleCustomer = (key: string) => {
    setForm(f => ({
      ...f,
      allowed_customers: f.allowed_customers.includes(key)
        ? f.allowed_customers.filter(k => k !== key)
        : [...f.allowed_customers, key],
    }));
  };

  return (
    <div className="animate-fadeIn max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Administration</h1>
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-200">User Management</h2>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 text-white text-sm font-medium hover:-translate-y-0.5 hover:shadow-lg transition-all"
        >
          {showCreate ? "Cancel" : "+ New User"}
        </button>
      </div>

      {msg && <div className="mb-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30 text-sm">{msg}</div>}

      {/* Create Form */}
      {showCreate && (
        <div className="mb-6 p-5 bg-[#1e2130] border border-[#2a2d3e] rounded-xl">
          <h3 className="text-base font-semibold text-gray-200 mb-4">Create New User</h3>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <input placeholder="Email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              className="px-3 py-2 rounded-lg border border-[#2a2d3e] bg-[#0f1117] text-sm outline-none focus:border-blue-500" />
            <input placeholder="Password" type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              className="px-3 py-2 rounded-lg border border-[#2a2d3e] bg-[#0f1117] text-sm outline-none focus:border-blue-500" />
            <input placeholder="Display Name" value={form.display_name} onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
              className="px-3 py-2 rounded-lg border border-[#2a2d3e] bg-[#0f1117] text-sm outline-none focus:border-blue-500" />
            <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
              className="px-3 py-2 rounded-lg border border-[#2a2d3e] bg-[#0f1117] text-sm outline-none focus:border-blue-500">
              <option value="viewer">Viewer</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="mb-3">
            <label className="text-xs text-gray-400 uppercase tracking-wider mb-2 block">Allowed Customers</label>
            <div className="flex flex-wrap gap-2">
              {customerKeys.map(ck => (
                <button key={ck.key} onClick={() => toggleCustomer(ck.key)}
                  className={`px-3 py-1 rounded-full text-xs border transition-all ${
                    form.allowed_customers.includes(ck.key)
                      ? "bg-blue-500 text-white border-blue-500"
                      : "border-[#2a2d3e] text-gray-400 hover:border-blue-500"
                  }`}>
                  {ck.name}
                </button>
              ))}
            </div>
          </div>
          <button onClick={handleCreate}
            className="px-4 py-2 rounded-lg bg-blue-500 text-white text-sm font-medium hover:bg-blue-600 transition-all">
            Create User
          </button>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-[#1e2130] border border-[#2a2d3e] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#1a1d27]">
              <th className="text-left px-4 py-3 text-xs text-gray-400 uppercase tracking-wider font-semibold">User</th>
              <th className="text-left px-4 py-3 text-xs text-gray-400 uppercase tracking-wider font-semibold">Role</th>
              <th className="text-left px-4 py-3 text-xs text-gray-400 uppercase tracking-wider font-semibold">Customers</th>
              <th className="text-left px-4 py-3 text-xs text-gray-400 uppercase tracking-wider font-semibold">Last Login</th>
              <th className="text-right px-4 py-3 text-xs text-gray-400 uppercase tracking-wider font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.user_id} className="border-t border-[#2a2d3e] hover:bg-[#252840] transition-colors">
                <td className="px-4 py-3">
                  <div className="font-medium">{u.display_name || u.email}</div>
                  <div className="text-xs text-gray-500">{u.email}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    u.role === "admin" ? "bg-purple-500/20 text-purple-400" : "bg-blue-500/20 text-blue-400"
                  }`}>
                    {u.role}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {(u.allowed_customers || []).slice(0, 4).map(c => (
                      <span key={c} className="px-1.5 py-0.5 rounded text-xs bg-[#2a2d3e] text-gray-300">{c}</span>
                    ))}
                    {(u.allowed_customers || []).length > 4 && (
                      <span className="text-xs text-gray-500">+{u.allowed_customers.length - 4}</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-gray-400">
                  {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : "Never"}
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => handleDelete(u.user_id, u.email)}
                    className="px-2 py-1 rounded text-xs text-red-400 hover:bg-red-500/10 transition-all">
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Budget Management — hidden for now, enable when needed
      <div className="mt-8">
        <h2 className="text-lg font-semibold mb-4">Budget Thresholds</h2>
        <div className="bg-[#1e2130] border border-[#2a2d3e] rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#1a1d27]">
                <th className="text-left px-4 py-3 text-xs text-gray-400 uppercase tracking-wider font-semibold">Customer</th>
                <th className="text-left px-4 py-3 text-xs text-gray-400 uppercase tracking-wider font-semibold">Monthly Budget</th>
                <th className="text-right px-4 py-3 text-xs text-gray-400 uppercase tracking-wider font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {customerKeys.map(ck => (
                <tr key={ck.key} className="border-t border-[#2a2d3e] hover:bg-[#252840] transition-colors">
                  <td className="px-4 py-2.5 font-medium">{ck.name}</td>
                  <td className="px-4 py-2.5">
                    {budgets[ck.key] ? (
                      <span className="text-emerald-400 font-medium">${Number(budgets[ck.key].amount).toLocaleString()}</span>
                    ) : (
                      <span className="text-gray-500">Not set</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <input
                      type="number"
                      placeholder="Amount"
                      className="w-28 px-2 py-1 rounded border border-[#2a2d3e] bg-[#0f1117] text-sm outline-none focus:border-blue-500 mr-2"
                      value={budgetForm.customer === ck.key ? budgetForm.amount : ""}
                      onChange={e => setBudgetForm({ customer: ck.key, amount: e.target.value })}
                    />
                    <button
                      onClick={async () => {
                        if (!budgetForm.amount || budgetForm.customer !== ck.key) return;
                        await api(`/api/budgets/${ck.key}`, { method: "PUT", body: JSON.stringify({ amount: parseFloat(budgetForm.amount) }) });
                        setBudgetForm({ customer: "", amount: "" });
                        load();
                      }}
                      className="px-2 py-1 rounded text-xs text-blue-400 hover:bg-blue-500/10 transition-all"
                    >Set</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      */}
    </div>
  );
}
